import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Building2, X, Check } from "lucide-react";

export default function PurchaseDialog({ open, onOpenChange, requirements, onDone }) {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState(null);
  const [supplierName, setSupplierName] = useState("");
  const [popOpen, setPopOpen] = useState(false);
  const [newSupplierOpen, setNewSupplierOpen] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: "", phone: "", gst: "" });
  const [addingSupplier, setAddingSupplier] = useState(false);

  const [lines, setLines] = useState([]); // {requirement_id, product, quantity, rate}
  const [paymentStatus, setPaymentStatus] = useState("unpaid");
  const [paymentMode, setPaymentMode] = useState("prepaid");
  const [paymentSources, setPaymentSources] = useState([]);
  const [paymentSourceId, setPaymentSourceId] = useState("");
  const [taxType, setTaxType] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const loadSuppliers = () =>
    api.get("/suppliers").then((r) => setSuppliers(r.data.items || [])).catch(() => {});
  const loadPaymentSources = () =>
    api.get("/payment-sources").then((r) => setPaymentSources(r.data.items || [])).catch(() => {});

  useEffect(() => {
    if (!open) return;
    loadSuppliers();
    loadPaymentSources();
    setSupplierId(null);
    setSupplierName("");
    setPaymentStatus("unpaid");
    setPaymentMode("prepaid");
    setPaymentSourceId("");
    setTaxType("");
    setDueDate("");
    setInvoiceNumber("");
    setNotes("");
    setLines(
      (requirements || []).map((r) => ({
        requirement_id: r.id,
        product: r.product_snapshot?.product || "—",
        brand: r.product_snapshot?.brand_name,
        auto_barcode: r.product_snapshot?.auto_barcode,
        quantity: r.quantity || 1,
        rate:
          Number(r.product_snapshot?.last_purchase_cost || r.product_snapshot?.purchase_price || 0) || 0,
      })),
    );
  }, [open, requirements]);

  const subtotal = useMemo(
    () => lines.reduce((acc, l) => acc + Number(l.quantity || 0) * Number(l.rate || 0), 0),
    [lines],
  );
  const total = subtotal;

  const updateLine = (idx, patch) =>
    setLines((arr) => arr.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const removeLine = (idx) => setLines((arr) => arr.filter((_, i) => i !== idx));

  const addSupplier = async () => {
    if (!newSupplier.name.trim()) return toast.error("Supplier name is required");
    setAddingSupplier(true);
    try {
      const { data } = await api.post("/suppliers", newSupplier);
      toast.success(`Added ${data.name}`);
      await loadSuppliers();
      setSupplierId(data.id);
      setSupplierName(data.name);
      setNewSupplierOpen(false);
      setNewSupplier({ name: "", phone: "", gst: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAddingSupplier(false);
    }
  };

  // COD implies unpaid until marked received; Credit is always an open due.
  useEffect(() => {
    if (paymentMode === "cod") {
      setPaymentStatus("unpaid");
      setPaymentSourceId("");
    } else if (paymentMode === "credit") {
      setPaymentStatus("unpaid");
      setPaymentSourceId("");
    }
  }, [paymentMode]);

  const submit = async () => {
    if (!taxType) return toast.error("Please select bill type — GST or Local");
    if (!supplierId) return toast.error("Please select or add a supplier");
    if (!lines.length) return toast.error("At least one line item is required");
    if (paymentMode === "prepaid" && paymentStatus === "paid" && !paymentSourceId) {
      return toast.error("Please select the payment source");
    }
    if (paymentMode === "prepaid" && paymentStatus === "unpaid" && !dueDate) {
      toast.error("Please set an expected delivery date");
      return;
    }
    setSaving(true);
    try {
      const body = {
        supplier_id: supplierId,
        line_items: lines.map((l) => ({
          requirement_id: l.requirement_id,
          quantity: Number(l.quantity || 1),
          rate: Number(l.rate || 0),
        })),
        payment_status: paymentStatus,
        payment_mode: paymentMode,
        payment_source_id: paymentSourceId || null,
        tax_type: taxType,
        due_date: dueDate || null,
        invoice_number: invoiceNumber || "",
        notes: notes || "",
      };
      const { data } = await api.post("/purchase-orders", body);
      toast.success(`Purchase recorded · ₹${data.total.toFixed(2)}`);
      // Trigger sidebar count refresh
      window.dispatchEvent(new Event("dyynit:sidebar-refresh"));
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden w-[95vw] sm:w-auto"
        data-testid="purchase-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Record purchase</DialogTitle>
          <DialogDescription>
            {lines.length} item(s) · attribute supplier, rates and payment details.
          </DialogDescription>
        </DialogHeader>

        {/* Supplier */}
        <div className="space-y-1 mt-2">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Supplier *</Label>
          <div className="flex gap-2 flex-wrap">
            <Popover open={popOpen} onOpenChange={setPopOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="flex-1 min-w-[240px] justify-start"
                  data-testid="purchase-supplier-trigger"
                >
                  <Building2 className="w-4 h-4 mr-2 text-[#9E6D5B]" />
                  {supplierName || "Select supplier"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[340px]" align="start">
                <Command>
                  <CommandInput placeholder="Search suppliers…" />
                  <CommandList>
                    <CommandEmpty>No suppliers. Use the button on the right to add one.</CommandEmpty>
                    <CommandGroup>
                      {suppliers.map((s) => (
                        <CommandItem
                          key={s.id}
                          onSelect={() => {
                            setSupplierId(s.id);
                            setSupplierName(s.name);
                            setPopOpen(false);
                          }}
                          data-testid={`purchase-supplier-opt-${s.id}`}
                          className="flex items-center justify-between"
                        >
                          <span className="truncate">{s.name}</span>
                          {supplierId === s.id && <Check className="w-3 h-3 text-[#9E6D5B]" />}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Button
              type="button"
              variant="outline"
              onClick={() => setNewSupplierOpen(true)}
              data-testid="purchase-add-supplier-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New
            </Button>
          </div>
        </div>

        {/* Line items */}
        <div className="mt-4">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Items</Label>
          <div className="border border-[hsl(var(--border))] rounded-md divide-y divide-[hsl(var(--border))] mt-1">
            {lines.map((l, i) => (
              <div
                key={l.requirement_id}
                className="p-3"
                data-testid={`purchase-line-${l.requirement_id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm leading-tight" title={l.product}>{l.product}</div>
                    <div className="text-xs text-muted-foreground font-mono-data truncate">
                      {l.brand} · {l.auto_barcode}
                    </div>
                  </div>
                  {lines.length > 1 && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="flex-shrink-0 h-7 w-7"
                      onClick={() => removeLine(i)}
                      data-testid={`purchase-remove-line-${l.requirement_id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Qty</div>
                    <Input
                      type="number"
                      min={1}
                      value={l.quantity}
                      onChange={(e) => updateLine(i, { quantity: Math.max(1, parseInt(e.target.value || "1")) })}
                      className="w-16 h-9 font-mono-data text-right"
                      data-testid={`purchase-line-qty-${l.requirement_id}`}
                    />
                  </div>
                  <div className="flex-shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Rate ₹</div>
                    <Input
                      type="number"
                      step="0.01"
                      min={0}
                      value={l.rate}
                      onChange={(e) => updateLine(i, { rate: parseFloat(e.target.value || "0") })}
                      className="w-24 h-9 font-mono-data text-right"
                      data-testid={`purchase-line-rate-${l.requirement_id}`}
                    />
                  </div>
                  <div className="text-right flex-shrink-0 ml-auto">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Amount</div>
                    <div className="font-mono-data text-sm font-semibold">
                      ₹{(Number(l.quantity || 0) * Number(l.rate || 0)).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment / tax / delivery */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Mode</Label>
            <Select value={paymentMode} onValueChange={setPaymentMode}>
              <SelectTrigger className="mt-1" data-testid="purchase-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="prepaid">Prepaid</SelectItem>
                <SelectItem value="cod">COD (pay on delivery)</SelectItem>
                <SelectItem value="credit">Credit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
            <Select
              value={paymentStatus}
              onValueChange={setPaymentStatus}
              disabled={paymentMode === "cod" || paymentMode === "credit"}
            >
              <SelectTrigger className="mt-1" data-testid="purchase-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="paid" disabled={paymentMode === "cod" || paymentMode === "credit"}>
                  Paid
                </SelectItem>
              </SelectContent>
            </Select>
            {paymentMode === "cod" && (
              <div className="text-[11px] text-muted-foreground mt-1">
                COD stays unpaid until marked received in Orders received.
              </div>
            )}
            {paymentMode === "credit" && (
              <div className="text-[11px] text-muted-foreground mt-1" data-testid="purchase-credit-hint">
                Credit mode is always unpaid — listed under Supplier pending dues.
              </div>
            )}
          </div>
          {paymentMode === "prepaid" && paymentStatus === "paid" ? (
            <div data-testid="purchase-source-wrap">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Payment source *</Label>
              <Select value={paymentSourceId} onValueChange={setPaymentSourceId}>
                <SelectTrigger className="mt-1" data-testid="purchase-source-select">
                  <SelectValue placeholder={paymentSources.length ? "Pick source" : "No sources added"} />
                </SelectTrigger>
                <SelectContent>
                  {paymentSources.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`purchase-source-opt-${s.id}`}>
                      {s.name}
                      {s.account_identifier ? ` · ${s.account_identifier}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {paymentSources.length === 0 && (
                <div className="text-[10px] text-muted-foreground mt-1">
                  Add one from <span className="underline">Payment sources</span> in the left menu.
                </div>
              )}
            </div>
          ) : (
            <div />
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bill type *</Label>
            <Select value={taxType} onValueChange={setTaxType}>
              <SelectTrigger className="mt-1" data-testid="purchase-tax-select">
                <SelectValue placeholder="Pick bill type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gst">GST bill</SelectItem>
                <SelectItem value="local">Local purchase</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Expected delivery date</Label>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="mt-1"
              data-testid="purchase-due-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Invoice #</Label>
            <Input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              className="mt-1 font-mono-data"
              data-testid="purchase-invoice-input"
            />
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 resize-y"
            placeholder="Anything specific — delivery terms, special pricing, etc."
            data-testid="purchase-notes-input"
          />
        </div>

        {/* Totals */}
        <div className="mt-4 rounded-md bg-[hsl(var(--muted))] p-3">
          <div className="flex justify-between text-sm">
            <span>Subtotal</span>
            <span className="font-mono-data">₹{subtotal.toFixed(2)}</span>
          </div>
          {taxType && taxType !== "none" && (
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Bill type</span>
              <span>{taxType === "gst" ? "GST bill" : "Local purchase"}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-semibold mt-1 pt-1 border-t border-[hsl(var(--border))]">
            <span>Total</span>
            <span className="font-mono-data text-[#9E6D5B]" data-testid="purchase-total">
              ₹{total.toFixed(2)}
            </span>
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="purchase-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !lines.length}
            style={{ backgroundColor: "#9E6D5B" }}
            data-testid="purchase-confirm-btn"
          >
            {saving ? "Saving…" : "Record purchase"}
          </Button>
        </DialogFooter>

        {/* Inline add-supplier dialog */}
        <Dialog open={newSupplierOpen} onOpenChange={setNewSupplierOpen}>
          <DialogContent className="sm:max-w-sm" data-testid="inline-supplier-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">New supplier</DialogTitle>
              <DialogDescription>Add a vendor on the fly.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name *</Label>
                <Input
                  value={newSupplier.name}
                  onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                  className="mt-1"
                  data-testid="inline-supplier-name-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
                <Input
                  value={newSupplier.phone}
                  onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                  className="mt-1 font-mono-data"
                  data-testid="inline-supplier-phone-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">GST</Label>
                <Input
                  value={newSupplier.gst}
                  onChange={(e) => setNewSupplier({ ...newSupplier, gst: e.target.value })}
                  className="mt-1 font-mono-data"
                  data-testid="inline-supplier-gst-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewSupplierOpen(false)}>Cancel</Button>
              <Button
                onClick={addSupplier}
                disabled={addingSupplier}
                style={{ backgroundColor: "#9E6D5B" }}
                data-testid="inline-supplier-add-btn"
              >
                {addingSupplier ? "Adding…" : "Add supplier"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
